import { useEffect, useRef, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Card from "react-bootstrap/Card";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaSpinner } from "react-icons/fa";
import { faHeart as regularHeart } from "@fortawesome/free-regular-svg-icons";
import { faHeart as solidHeart } from "@fortawesome/free-solid-svg-icons";

import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";

// Redux actions
import { fetchRelatedProductsProducts, fetchRelatedProducts } from "../../../redux/features/productSlice";
import { addToCart, updateCart } from "../../../redux/features/cartSlice";
import { addToWhishlist } from "../../../redux/features/addToWhishlistSlice";
import { removeWishItem } from "../../../redux/features/getWishlistSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import LoginModal from "../../../modal/LoginModal";
import RegisterModal from "../../../modal/RegisterModal";
import ForgotPasswordModal from "../../../modal/ForgotPasswordModal";
import VerifyOtpModal from "../../../modal/VerifyOtpModal";
import { Rating } from "@mui/material";
import { auto } from "@popperjs/core";
import { timers } from "jquery";
import { useModal } from "../../../hooks/ModalContext";

function RelatedProducts({ category_id }) {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.language.lang);
  const { RelatedProducts } = useSelector((state) => state.product);
  const { t } = useTranslation();

  // Redux state
  const { api_token } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);
  const [cartProducts, setCartProducts] = useState({});
  const [wishlistedProducts, setWishlistedProducts] = useState({});

  const { activeModal, setActiveModal, emailForOtp, setEmailForOtp } = useModal();

  const { emailForOtp: emailForOtpLogin } = useSelector((state) => state.auth);
  const { emailForOtp: emailForOtpRegister } = useSelector(
    (state) => state.register
  );

  // 🔹 Fetch New Arrival Products
  useEffect(() => {
    if (!category_id) return;

    setLoading(true);
    dispatch(fetchRelatedProducts({
      category_id,
      lang,
    })).unwrap()
      .catch((error) => console.error("Error fetching product details:", error))
      .finally(() => setLoading(false));
  }, [dispatch, lang]);

  // 🔹 Animate On Scroll
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  // --- Cart / Wishlist Handlers (Optimized) ---
  const handleAddToCart = async (productId) => {
    if (!api_token) {
      toast.error(`${t("You_need_to_login_to_add_product_to_your_cart")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
      setActiveModal("login");
      return;
    }

    try {
      setCartProducts((prev) => ({ ...prev, [productId]: true }));
      await dispatch(addToCart({ product_id: productId })).unwrap();
      toast.success(`${t("Product_added_to_your_cart_successfully")}🛒`, {
        position: "top-right",
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

        autoClose: 2000,
      });
    } catch (error) {
      toast.error(`${t("Failed_to_add_product_to_cart")}`, {
        position: "top-right",
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

        autoClose: 2000,
      });
      console.error("Add to Cart Error:", error);
    }
  };

  const handleUpdateCart = async (product_id, newQuantity = 1) => {
    if (newQuantity < 1) {
      toast.error(`${t("Quantity_must_be_at_least_1")}.`, {
        position: "top-right",
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

        autoClose: 2000,
      });
      return;
    }

    try {
      await dispatch(updateCart({ product_id, quantity: newQuantity })).unwrap();
      toast.success(`${t("Cart_updated_successfully")} ✅`, {
        position: "top-right",
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

        autoClose: 2000,
      });
    } catch (error) {
      toast.error(`${t("Failed_to_update_cart")} ❌`, {
        position: "top-right",
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

        autoClose: 2000,
      });
      console.error("Update Cart Error:", error);
    }
  };

  const handleAddToWishlist = async (productId) => {
    if (!api_token) {
      toast.error(`${t("You_need_to_login_to_add_product_to_your_wishlist")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
      setActiveModal("login");
      return;
    }

    try {
      setWishlistedProducts((prev) => ({
        ...prev,
        [productId]: !prev[productId],
      }));
      await dispatch(addToWhishlist({ product_id: productId })).unwrap();
      toast.success(`${t("Product_added_to_your_wishlist")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
    } catch (error) {
      toast.error(`${t("Failed_to_add_product_to_wishlist")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
      console.error("Add to Wishlist Error:", error);
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    if (!api_token) {
      toast.error(`${t("You_need_to_login_to_remove_product_from_wishlist")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
      setActiveModal("login");
      return;
    }

    try {
      setWishlistedProducts((prev) => ({ ...prev, [productId]: false }));
      await dispatch(removeWishItem(productId)).unwrap();
      toast.success(`${t("Product_removed_from_your_wishlist")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
    } catch (error) {
      toast.error(`${t("Failed_to_remove_product_from_wishlist")}`, {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
      console.error("Remove from Wishlist Error:", error);
    }
  };

  // 🔹 Slider settings
  const settings = {
    dots: false,
    infinite: true,
    // autoplay: true,
    // autoplaySpeed: 3000,
    speed: 500,
    slidesToShow: 6,
    slidesToScroll: 1,
    arrows: false,
    beforeChange: (_, next) => setCurrentSlide(next),
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 768, settings: { slidesToShow: 2 } },
    ],
  };

  const products = RelatedProducts?.records || [];

  if (loading) {
    return (
      <div className="text-center my-5">
        <FaSpinner className="spin fs-2" />
      </div>
    );
  }

  return (
    <>
      <h3 className="container px-4 text-bold">{t("Related_Products")}</h3>
      <div className="container p-0 px-md-4">
        {products.length > 0 ? (
          <Slider ref={sliderRef} {...settings}>
            {products.map((item) => {
              const firstImage =
                item.image?.[0] || "/placeholder-image.jpg";

              const inWishlist =
                wishlistedProducts[item.id] || item.in_whishlist || false;
              const inCart =
                cartProducts[item.id] || item.in_cart || false;

              return (
                <div key={item.id} className="px-2 col-6 col-xl-2">
                  <div
                    className="card rounded-3 shadow-sm position-relative bg-white h-100"
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    {/* Wishlist Icon */}
                    <FontAwesomeIcon
                      icon={inWishlist ? solidHeart : regularHeart}
                      className="favorite-icon"
                      onClick={() =>
                        inWishlist
                          ? handleRemoveFromWishlist(item.id)
                          : handleAddToWishlist(item.id)
                      }
                    />

                    {/* Product Image */}
                    <Link to={`/product/${item.id}`}>
                      <div
                        className="position-relative d-flex justify-content-center align-items-center p-3"
                        style={{ height: "220px" }}
                      >
                        <img
                          className="card-img-top"
                          alt="No Image"
                          onError={(e) => {
                            e.target.onerror = null; // prevents infinite loop
                            e.target.src = "https://amzio.de/api/uploads/products/no-thumbnail.png";
                          }}
                          src={firstImage}
                          style={{ maxHeight: "100%", objectFit: "contain" }}
                        />
                      </div>
                    </Link>

                    {/* Product Details */}
                    <div
                      className="card-body d-flex flex-column "
                      style={{ flexGrow: 1 }}
                    >
                      <div className="text-start w-100">
                        <Link to={`/product/${item.id}`}>
                          {/* Fixed height title for equal card height */}
                          <h6
                            className="card-title text-start text-dark fs-6 fw-bold d-block three-line-truncate mb-2"
                            style={{
                              fontSize: "14px",
                              minHeight: "48px",          // Locks space for 3 lines
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden"
                            }}
                          >
                            {item.title}
                          </h6>

                          <div className="price-section pb-2 d-flex align-items-center gap-2">
                            {/* Higher price */}
                            {item?.mrp && (
                              <span className="text-muted text-decoration-line-through">€{item.mrp}</span>
                            )}
                            {/* Original price (strikethrough) */}
                            <span className="hign-price fw-bold fs-6">
                              €{item.sales_price}
                            </span>
                          </div>
                        </Link>
                      </div>

                      {/* Action Buttons */}
                      <div className="d-flex justify-content-center flex-column flex-sm-row pt-3 w-100">
                        <button
                          className={`btn-stylish me-sm-2 d-flex align-items-center justify-content-center mb-2 mb-sm-0 w-100 ${inCart ? "disabled" : ""
                            }`}
                          onClick={() =>
                            inCart
                              ? handleUpdateCart(item.id, item.quantity + 1)
                              : handleAddToCart(item.id)
                          }
                        >
                          {inCart ? "+ " : `${t("add_to_cart")}`}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </Slider>
        ) : (
          <p className="text-center text-muted">{t("No products available")}</p>
        )}

        {/* Progress Bar & Controls */}
        {products.length > 0 && (
          <div className="row progressbar-main d-flex justify-content-center align-items-center">
            <button
              className="bg-gray-500 py-1 rounded-l progressbar-button col-2 col-md-1"
              onClick={() => sliderRef.current.slickPrev()}
            >
              <ArrowBackIos />
            </button>

            <div className="progressbar position-relative col-8 col-md-3 bg-gray-300 h-2 rounded ps-0">
              <div
                className="progressbar position-absolute h-full transition-all duration-500 rounded"
                style={{
                  width: `${((currentSlide + 1) / products.length) * 100}%`,
                  backgroundColor: "var(--secondary-light-color)",
                }}
              />
            </div>

            <button
              className="bg-gray-500 py-1 rounded-r progressbar-button col-1"
              onClick={() => sliderRef.current.slickNext()}
            >
              <ArrowForwardIos />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default RelatedProducts;
